import { useContext, useState, useRef, useEffect, useCallback } from "react";
import { AuthContext } from "../context/AuthContext";
import { useDarkMode } from "../context/DarkModeContext";
import { useLocation, Link } from "react-router-dom";
import uasLogo from '../image/UASLOGO2.png';

const Header = () => {
  const { user, logout } = useContext(AuthContext);
  const { darkMode, toggleDarkMode } = useDarkMode();
  const location = useLocation();

  // ✅ CHECK IF WE'RE ON HOMEPAGE
  const isOnHomepage = location.pathname === '/home' || location.pathname === '/' || location.pathname === '';

  // ─── Notifications state ───
  const [isNotificationOpen, setIsNotificationOpen] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [notificationCount, setNotificationCount] = useState(0);
  const [lastChecked, setLastChecked] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [fetchError, setFetchError] = useState(null);

  // ✅ PREVENT MULTIPLE CONCURRENT REQUESTS
  const fetchingRef = useRef(false);
  const intervalRef = useRef(null);

  const notificationRef = useRef(null);
  const dropdownRef = useRef(null);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);

  // ─── Format "x minutes ago" ───
  const formatTime = (timestamp) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diffInMinutes = Math.floor((now - date) / (1000 * 60));
    if (diffInMinutes < 1) return 'Just now';
    if (diffInMinutes < 60) return `${diffInMinutes}m ago`;
    if (diffInMinutes < 1440) return `${Math.floor(diffInMinutes / 60)}h ago`;
    return `${Math.floor(diffInMinutes / 1440)}d ago`;
  };

  // ✅ ENHANCED FETCH WITH ROUTE CHECKING AND ERROR HANDLING
  const fetchNotifications = useCallback(async (force = false) => {
    // ✅ ONLY FETCH IF ON HOMEPAGE OR FORCED (manual click)
    if (!force && !isOnHomepage) {
      return;
    }

    // Prevent concurrent requests
    if (fetchingRef.current) {
      return;
    }

    fetchingRef.current = true;
    setIsLoading(true);
    setFetchError(null);

    try {
      
      const response = await fetch('http://localhost:5000/api/logs/fetch', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        signal: AbortSignal.timeout(10000) // 10 second timeout
      });

      if (!response.ok) {
        if (response.status === 429) {
          throw new Error('Rate limited. Please wait before refreshing.');
        }
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const data = await response.json();

      if (data.success && Array.isArray(data.documents)) {
        setNotifications(data.documents);

        // Count how many logs have timestamp > lastChecked (and within 24h)
        const now = new Date();
        const twentyFourH = new Date(now.getTime() - 24 * 60 * 60 * 1000);

        const unread = data.documents.filter(log => {
          const logDate = new Date(log.timestamp);
          return (
            logDate > twentyFourH &&
            (!lastChecked || logDate > new Date(lastChecked))
          );
        }).length;

        setNotificationCount(unread);
      } else {
        setNotifications([]);
        setNotificationCount(0);
      }
    } catch (err) {
      console.error('❌ Error fetching notifications:', err.message);
      setFetchError(err.message);
      
      // Don't clear existing notifications on error, just show error state
    } finally {
      setIsLoading(false);
      fetchingRef.current = false;
    }
  }, [isOnHomepage, lastChecked]);

  // ─── 2) Mark all as read (set lastChecked to "now", zero badge) ───
  const markAsRead = () => {
    const nowISO = new Date().toISOString();
    setLastChecked(nowISO);
    setNotificationCount(0);
    localStorage.setItem('lastNotificationCheck', nowISO);
  };

  // ─── 3) On mount, load lastChecked from localStorage ───
  useEffect(() => {
    const stored = localStorage.getItem('lastNotificationCheck');
    if (stored) {
      setLastChecked(stored);
    }
  }, []);

  // ✅ SMART POLLING - ONLY ON HOMEPAGE
  useEffect(() => {
    // Clear any existing interval
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }

    if (isOnHomepage) {
      
      // Initial fetch
      fetchNotifications();
      
      // Set up polling every 30 seconds (increased from 10 to reduce load)
      intervalRef.current = setInterval(() => {
        fetchNotifications();
      }, 30000);
    } else {
    }

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, [isOnHomepage, fetchNotifications]);

  // ✅ CLEANUP ON UNMOUNT
  useEffect(() => {
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, []);

  // ─── 5) Close dropdowns if clicking outside ───
  useEffect(() => {
    function handleClickOutside(event) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsDropdownOpen(false);
      }
      if (notificationRef.current && !notificationRef.current.contains(event.target)) {
        setIsNotificationOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // ✅ MANUAL NOTIFICATION REFRESH
  const handleNotificationClick = async () => {
    if (!isNotificationOpen) {
      // Opening - fetch fresh notifications regardless of route
      await fetchNotifications(true); // Force fetch
      markAsRead();
    }
    setIsNotificationOpen(v => !v);
  };

  return (
    <nav className="fixed top-0 z-50 w-full font-montserrat">
      {/* Gradient background layer */}
      <div className="absolute inset-0 bg-gradient-to-r from-white/90 via-white/80 to-gray-50/70 dark:from-[#2D2D2D]/90 dark:via-[#2D2D2D]/80 dark:to-[#2D2D2D]/70"></div>
      
      {/* Glass effect layer */}
      <div className="absolute inset-0 backdrop-blur-xl backdrop-saturate-150 bg-white/30 dark:bg-[#2D2D2D]/30"></div>
      
      {/* Subtle gradient overlay */}
      <div className="absolute inset-0 bg-gradient-to-b from-transparent via-transparent to-white/10 dark:to-[#2D2D2D]/10"></div>
      
      {/* Bottom border gradient */}
      <div className="absolute bottom-0 left-0 right-0 h-[1px] bg-gradient-to-r from-transparent via-gray-300/50 to-transparent dark:via-transparent"></div>
      
      <div className="relative px-3 py-2 lg:px-5">
        <div className="flex items-center justify-between">

          {/* ─── Left side: Logo with glass effect ─── */}
          <div className="flex items-center justify-start rtl:justify-end">
            <Link to="/" className="group flex items-center space-x-2 px-3 py-1.5 rounded-xl bg-white/60 backdrop-blur-lg dark:bg-transparent border border-white/20 dark:border-transparent shadow-sm hover:shadow-md transition-all duration-300 hover:scale-105 hover:bg-white/70 dark:hover:bg-transparent">
              <div className="relative">
                <img src={uasLogo} className="relative h-6 w-auto" alt="UAS Logo" />
              </div>
              <span className="relative font-semibold text-sm text-gray-500 dark:text-white">
                NEURADESK
              </span>
            </Link>
          </div>

          {/* ─── Right side: Actions with glass buttons ─── */}
          <div className="flex items-center space-x-2">

            {/* ✅ ENHANCED Glassmorphic Notification bell */}
            <div className="relative" ref={notificationRef}>
              <button
                type="button"
                onClick={handleNotificationClick}
                disabled={isLoading}
                className="
                  relative w-9 h-9 flex items-center justify-center rounded-full
                  bg-gray-200 dark:bg-laryzeDarkGray backdrop-blur-md
                  shadow-sm dark:shadow-xl transition-all duration-300
                  hover:scale-110 hover:bg-gray-300 dark:hover:bg-laryzeDarkGray
                  disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100
                  text-gray-900 dark:text-uasYellow
                  group overflow-hidden
                "
                aria-expanded={isNotificationOpen}
                title={fetchError ? `Error: ${fetchError}` : 'Notifications'}
              >
                {/* Animated gradient background */}
                <div className="absolute inset-0 bg-gradient-to-br from-purple-500/10 via-pink-500/10 to-orange-500/10 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                
                <span className="sr-only">Open notifications</span>
                
                {/* ✅ Loading indicator */}
                {isLoading ? (
                  <div className="relative animate-spin rounded-full h-3.5 w-3.5 border-2 border-transparent border-t-purple-500 border-r-pink-500"></div>
                ) : (
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="18"
                    height="18"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    className="relative transition-colors duration-300"
                  >
                    <path d="M18 8a6 6 0 00-12 0c0 7-3 9-3 9h18s-3-2-3-9" />
                    <path d="M13.73 21a2 2 0 01-3.46 0" />
                  </svg>
                )}

                {/* ✅ Enhanced badge with glow effect */}
                {fetchError ? (
                  <span className="
                    absolute -top-0.5 -right-0.5 bg-gradient-to-br from-red-500 to-red-600 text-white text-[10px]
                    rounded-full h-4 w-4 flex items-center justify-center font-bold
                    shadow-sm shadow-red-500/50 animate-pulse
                  ">
                    !
                  </span>
                ) : notificationCount > 0 && (
                  <span className="
                    absolute -top-0.5 -right-0.5 bg-gradient-to-br from-red-500 to-pink-500 text-white text-[10px]
                    rounded-full h-4 w-4 flex items-center justify-center font-bold
                    shadow-sm shadow-red-500/50 animate-bounce
                  ">
                    {notificationCount > 9 ? '9+' : notificationCount}
                  </span>
                )}
              </button>

              {/* ✅ Glassmorphic dropdown */}
              {isNotificationOpen && (
                <div className="
                  absolute right-0 z-50 mt-2 w-80
                  bg-white dark:bg-gray-700 rounded-sm shadow-lg 
                  border border-gray-200 dark:border-gray-600
                  animate-in fade-in slide-in-from-top-5 duration-300
                ">
                  {/* Header */}
                  <div className="px-4 py-2.5 border-b border-gray-200 dark:border-gray-600">
                    <div className="flex items-center justify-between">
                      <h3 className="text-sm font-semibold text-gray-900 dark:text-white">
                        System Notifications
                      </h3>
                      {!isOnHomepage && (
                        <span className="text-[10px] font-medium text-amber-600 dark:text-amber-400">
                          Auto-refresh paused
                        </span>
                      )}
                    </div>
                  </div>

                  {/* ✅ Error display */}
                  {fetchError && (
                    <div className="px-4 py-2 bg-red-50 dark:bg-red-900/20 border-b border-red-200 dark:border-red-800">
                      <p className="text-xs font-medium text-red-700 dark:text-red-400">
                        ⚠️ {fetchError}
                      </p>
                    </div>
                  )}

                  {/* Notifications list */}
                  <div className="max-h-80 overflow-y-auto">
                    {notifications.length > 0 ? (
                      notifications.map((log, idx) => (
                        <div
                          key={idx}
                          className="
                            px-4 py-3 border-b border-gray-100 dark:border-gray-600
                            hover:bg-gray-50 dark:hover:bg-gray-600
                            transition-colors cursor-pointer group
                          "
                        >
                          <div className="flex items-start space-x-2">
                            {/* Animated dot */}
                            <div className="flex-shrink-0 w-1.5 h-1.5 rounded-full mt-1.5 bg-gray-500"></div>

                            <div className="flex-1 min-w-0">
                              {/* Message */}
                              <p className="text-xs font-medium text-gray-900 dark:text-white truncate">
                                {log.description}
                              </p>

                              {/* Meta info */}
                              <div className="flex items-center justify-between mt-1">
                                <span className="text-[10px] text-gray-500 dark:text-gray-400 font-medium">
                                  {log.actor}
                                </span>
                                <span className="text-[10px] text-gray-500 dark:text-gray-400">
                                  {formatTime(log.timestamp)}
                                </span>
                              </div>

                              {/* Action type badge */}
                              <p className="text-[10px] italic text-gray-600 dark:text-gray-300 mt-1 truncate">
                                {log.actionType}
                              </p>
                            </div>
                          </div>
                        </div>
                      ))
                    ) : (
                      <div className="px-4 py-8 text-center">
                        <p className="text-xs font-medium text-gray-500 dark:text-gray-400">
                          {isLoading ? 'Loading notifications...' : 'No notifications yet'}
                        </p>
                      </div>
                    )}
                  </div>

                  {/* Footer */}
                  {notifications.length > 0 && (
                    <div className="px-4 py-2 border-t border-gray-200 dark:border-gray-600">
                      <button
                        onClick={() => setIsNotificationOpen(false)}
                        className="text-xs font-medium text-yellow-600 dark:text-yellow-400 hover:underline w-full text-center"
                      >
                        View All Logs
                      </button>
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* ─── Glassmorphic Dark mode toggle ─── */}
            <div className="flex items-center">
              <label className="relative inline-flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  checked={darkMode}
                  onChange={toggleDarkMode}
                  className="sr-only peer"
                />
                <div className="
                  w-9 h-9 flex items-center justify-center rounded-full
                  transition-colors duration-200 ease-in-out
                  bg-gray-200 peer-checked:bg-laryzeDarkGray dark:shadow-lg
                  text-gray-900 peer-checked:text-uasYellow
                ">
                  {darkMode ? (
                    <svg 
                      xmlns="http://www.w3.org/2000/svg" 
                      className="h-5 w-5" 
                      fill="none" 
                      viewBox="0 0 24 24" 
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M20.354 15.354A9 9 0 018.646 3.646 9.003 9.003 0 0012 21a9.003 9.003 0 008.354-5.646z"
                      />
                    </svg>
                  ) : (
                    <svg 
                      xmlns="http://www.w3.org/2000/svg" 
                      className="h-5 w-5" 
                      fill="none" 
                      viewBox="0 0 24 24" 
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364 6.364l-.707-.707M6.343 6.343l-.707-.707m12.728 0l-.707.707M6.343 17.657l-.707.707M16 12a4 4 0 11-8 0 4 4 0 018 0z"
                      />
                    </svg>
                  )}
                </div>
              </label>
            </div>

            {/* ─── Glassmorphic User avatar dropdown ─── */}
            <div className="relative" ref={dropdownRef}>
              <button
                type="button"
                onClick={() => setIsDropdownOpen(v => !v)}
                className="
                  relative w-9 h-9 flex items-center justify-center rounded-full
                  bg-gray-200 dark:bg-laryzeDarkGray backdrop-blur-md
                  shadow-sm dark:shadow-xl transition-all duration-300
                  hover:scale-110 hover:bg-gray-300 dark:hover:bg-laryzeDarkGray
                  text-gray-900 dark:text-uasYellow
                  group overflow-hidden
                "
                aria-expanded={isDropdownOpen}
              >
                {/* Animated gradient background */}
                <div className="absolute inset-0 bg-gradient-to-br from-blue-500/10 via-purple-500/10 to-pink-500/10 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                
                <span className="sr-only">Open user menu</span>
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="18"
                  height="18"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  className="relative transition-colors duration-300"
                >
                  <path d="M19 21v-2a4 4 0 0 0-4-4H9a4 4 0 0 0-4 4v2" />
                  <circle cx="12" cy="7" r="4" />
                </svg>
              </button>

              {/* Glassmorphic dropdown menu */}
              {isDropdownOpen && (
                <div className="
                  absolute right-0 z-50 mt-2 w-48 text-base list-none
                  bg-white divide-y divide-gray-100 rounded-sm shadow-sm
                  dark:bg-gray-700 dark:divide-gray-600
                  animate-in fade-in slide-in-from-top-5 duration-300
                ">
                  <div className="px-4 py-3" role="none">
                    <p className="text-sm text-gray-900 dark:text-white truncate" role="none">
                      {user?.name || "Guest"}
                    </p>
                  </div>
                  <ul className="py-1" role="none">
                    <li>
                      <Link
                        to="/home"
                        onClick={() => setIsDropdownOpen(false)}
                        className={`
                          block px-4 py-2 text-sm hover:bg-gray-100 dark:hover:bg-gray-600 dark:hover:text-white
                          ${location.pathname === '/home'
                            ? 'text-gray-900 dark:text-uasYellow'
                            : 'text-gray-700 dark:text-gray-300'
                          }
                        `}
                        role="menuitem"
                      >
                        Dashboard
                      </Link>
                    </li>
                    <li>
                      <Link
                        to="/settings"
                        onClick={() => setIsDropdownOpen(false)}
                        className={`
                          block px-4 py-2 text-sm hover:bg-gray-100 dark:hover:bg-gray-600 dark:hover:text-white
                          ${location.pathname === '/settings'
                            ? 'text-gray-900 dark:text-uasYellow'
                            : 'text-gray-700 dark:text-gray-300'
                          }
                        `}
                        role="menuitem"
                      >
                        Settings
                      </Link>
                    </li>
                    {(user?.role === "team leader" || user?.role === "manager") && (
                      <li>
                        <Link
                          to="/idp-monitoring"
                          onClick={() => setIsDropdownOpen(false)}
                          className={`
                            block px-4 py-2 text-sm hover:bg-gray-100 dark:hover:bg-gray-600 dark:hover:text-white
                            ${location.pathname === '/idp-monitoring'
                              ? 'text-gray-900 dark:text-uasYellow'
                              : 'text-gray-700 dark:text-gray-300'
                            }
                          `}
                          role="menuitem"
                        >
                          Reports
                        </Link>
                      </li>
                    )}
                    <li>
                      <button
                        onClick={() => {
                          setIsDropdownOpen(false);
                          logout();
                        }}
                        className="block w-full text-left px-4 py-2 text-sm text-red-600 dark:text-red-400 hover:bg-gray-100 dark:hover:bg-gray-600"
                        role="menuitem"
                      >
                        Sign out
                      </button>
                    </li>
                  </ul>
                </div>
              )}
            </div>

          </div>
        </div>
      </div>
    </nav>
  );
};

export default Header;